import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  CheckCircle2, HelpCircle, XCircle, MessageSquare, RefreshCw,
  Wand2, CheckCheck, Ban, Clock, Zap,
} from 'lucide-react';
import { ResolveFeedbackDialog } from './ResolveFeedbackDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VOTE_ICON = { looks_good: CheckCircle2, question: HelpCircle, drop: XCircle };
const VOTE_STYLE = {
  looks_good: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  question:   'bg-amber-50 text-amber-700 border-amber-200',
  drop:       'bg-rose-50 text-rose-700 border-rose-200',
};

function crStatusBadge(cr) {
  if (!cr) return null;
  if (cr.decision === 'accepted') return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] gap-1" variant="outline"><CheckCheck className="w-3 h-3" /> Accepted</Badge>;
  if (cr.decision === 'declined') return <Badge className="bg-rose-50 text-rose-700 border-rose-200 text-[10px] gap-1" variant="outline"><Ban className="w-3 h-3" /> Declined</Badge>;
  return <Badge className="bg-sky-50 text-sky-700 border-sky-200 text-[10px] gap-1" variant="outline"><Clock className="w-3 h-3" /> Awaiting reply</Badge>;
}

export function StakeholderFeedbackSummary({ estimateId, refreshKey = 0, onEstimateMutated }) {
  const [feedbackData, setFeedbackData] = useState(null);
  const [changeRequests, setChangeRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolveRow, setResolveRow] = useState(null);
  const [applyingId, setApplyingId] = useState(null);

  const load = useCallback(async () => {
    if (!estimateId) return;
    setLoading(true);
    try {
      const [fRes, cRes] = await Promise.all([
        axios.get(`${API_URL}/api/scope-pack/feedback`, {
          params: { estimate_id: estimateId }, withCredentials: true,
        }),
        axios.get(`${API_URL}/api/change-requests`, {
          params: { estimate_id: estimateId }, withCredentials: true,
        }),
      ]);
      setFeedbackData(fRes.data);
      setChangeRequests(cRes.data?.change_requests || []);
    } catch {
      setFeedbackData(null);
      setChangeRequests([]);
    } finally { setLoading(false); }
  }, [estimateId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const applyCr = async (cr) => {
    if (!cr?.cr_id) return;
    setApplyingId(cr.cr_id);
    try {
      const res = await axios.post(`${API_URL}/api/change-requests/${cr.cr_id}/apply`,
        {}, { withCredentials: true });
      if (res.data?.already_applied) {
        toast.info('This change was already applied.');
      } else if (res.data?.changed) {
        toast.success(res.data.summary || 'Applied to estimate.');
        if (onEstimateMutated) onEstimateMutated();
      } else {
        toast.info(res.data?.summary || 'Logged to activity; no scope change.');
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply change request');
    } finally {
      setApplyingId(null);
    }
  };

  // Index CRs by (feedback_id, component_name) and fall back to (component_name) if no feedback_id match.
  const crIndex = useMemo(() => {
    const m = new Map();
    for (const cr of changeRequests) {
      const key = `${cr.feedback_id || ''}::${cr.component_name}`;
      // Keep the most recent CR per key
      if (!m.has(key) || new Date(cr.created_at) > new Date(m.get(key).created_at)) {
        m.set(key, cr);
      }
    }
    return m;
  }, [changeRequests]);

  if (!estimateId) return null;
  if (!feedbackData || feedbackData.count === 0) return null;

  return (
    <>
      <Card className="border-slate-200" data-testid="stakeholder-feedback-summary">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-[#0F2040] flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Stakeholder Feedback
            <Badge variant="secondary" className="ml-2 text-[10px] bg-slate-100 text-slate-600">
              {feedbackData.count} submission{feedbackData.count === 1 ? '' : 's'}
            </Badge>
            <Button type="button" variant="ghost" size="sm" onClick={load}
              className="ml-auto h-6 px-2 text-[10px] text-slate-500 hover:text-[#0F2040]"
              data-testid="feedback-summary-refresh">
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {(feedbackData.feedback || []).map(fb => (
            <div key={fb.feedback_id} className="border border-slate-100 rounded-md p-2.5">
              <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-2">
                <span className="font-medium text-slate-700">
                  {fb.recipient_name || fb.recipient_email}
                </span>
                <span className="text-slate-300">&middot;</span>
                <span>{new Date(fb.submitted_at).toLocaleString()}</span>
              </div>
              <div className="space-y-1.5">
                {(fb.items || []).map(it => {
                  const Icon = VOTE_ICON[it.vote] || CheckCircle2;
                  const cr = crIndex.get(`${fb.feedback_id}::${it.component_name}`);
                  const showResolve = (it.vote === 'question' || it.vote === 'drop') && !cr;
                  return (
                    <div key={it.component_name} className="flex flex-wrap items-start gap-2 text-xs py-1.5 px-2 rounded bg-slate-50/60">
                      <Badge variant="outline" className={`${VOTE_STYLE[it.vote]} text-[10px] gap-1 px-1.5 py-0.5 shrink-0`}>
                        <Icon className="w-3 h-3" />
                        {it.vote === 'looks_good' ? 'Good' : it.vote === 'question' ? 'Question' : 'Drop'}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#0F2040] truncate">{it.component_name}</div>
                        {it.note && (
                          <div className="text-[11px] text-slate-600 italic mt-0.5">&ldquo;{it.note}&rdquo;</div>
                        )}
                        {cr && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1">
                            {crStatusBadge(cr)}
                            <span className="text-[11px] text-slate-600 flex-1 min-w-0 truncate">
                              <span className="text-slate-400">Proposed: </span>
                              {cr.proposed_note}
                            </span>
                            {cr.decision_note && (
                              <span className="text-[10px] text-slate-500 w-full italic">
                                Reply: &ldquo;{cr.decision_note}&rdquo;
                              </span>
                            )}
                            {cr.decision === 'accepted' && !cr.applied_at && (
                              <Button size="sm" variant="outline"
                                className="h-6 px-2 text-[10px] border-emerald-600 text-emerald-700 hover:bg-emerald-50 shrink-0 gap-1"
                                disabled={applyingId === cr.cr_id}
                                onClick={() => applyCr(cr)}
                                data-testid={`apply-cr-${cr.cr_id}`}>
                                {applyingId === cr.cr_id
                                  ? <RefreshCw className="w-3 h-3 animate-spin" />
                                  : <Zap className="w-3 h-3" />}
                                Apply to Estimate
                              </Button>
                            )}
                            {cr.applied_at && (
                              <Badge className="bg-emerald-600 text-white border-emerald-600 text-[10px] gap-1" variant="outline">
                                <CheckCheck className="w-3 h-3" /> Applied
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      {showResolve && (
                        <Button size="sm" variant="outline"
                          className="h-6 px-2 text-[10px] border-[#0F2040] text-[#0F2040] hover:bg-[#0F2040]/5 shrink-0"
                          data-testid={`resolve-btn-${fb.feedback_id}-${it.component_name.replace(/\s/g, '-')}`}
                          onClick={() => setResolveRow({
                            ...it,
                            from: fb.recipient_name || fb.recipient_email,
                            estimateId,
                            estimateName: fb.estimate_name || '',
                            feedbackId: fb.feedback_id,
                            sendId: fb.send_id,
                            stakeholderEmail: fb.recipient_email,
                            stakeholderName: fb.recipient_name,
                          })}>
                          <Wand2 className="w-3 h-3 mr-1" /> Resolve
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
              {fb.overall_note && (
                <div className="mt-2 bg-slate-50 border border-slate-100 rounded-md p-2">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">Overall note</div>
                  <div className="text-[11px] text-slate-700 whitespace-pre-wrap">{fb.overall_note}</div>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <ResolveFeedbackDialog
        open={Boolean(resolveRow)}
        onOpenChange={(o) => !o && setResolveRow(null)}
        row={resolveRow}
        onResolved={load}
      />
    </>
  );
}
